import { apiSlice } from './apiSlice';
import { decodeToken, encodeToken } from '../../utils/TokenUtils';

export const emergencyButtonApi = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getAllEmergencyButtons: builder.query({
			query: () => ({
				url: `/api/v1/emergency-buttons`,
				params: { _ts: Date.now() },
			}),
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data ?? response?.message ?? []);
			},
			providesTags: ['EmergencyButton'],
		}),
		getEmergencyButtonById: builder.query({
			query: (emergencyButtonId) => `/api/v1/emergency-buttons/${encodeURIComponent(emergencyButtonId)}`,
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data ?? response?.message ?? null);
			},
			providesTags: (result, error, id) => [{ type: 'EmergencyButton' as const, id }, 'EmergencyButton'],
		}),
		getEmergencyButtonByImei: builder.query({
			query: (imei) => ({
				url: `/api/v1/emergency-buttons/${encodeURIComponent(String(imei || '').trim())}`,
				params: { _ts: Date.now() },
			}),
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data ?? response?.message ?? null);
			},
			providesTags: (result, error, imei) => [{ type: 'EmergencyButton' as const, id: imei }, 'EmergencyButton'],
		}),
		getAllEmergencyButtonsToCenter: builder.query({
			query: (centerId) => ({
				url: `/api/v1/emergency-buttons/${encodeURIComponent(centerId)}`,
				params: { _ts: Date.now() },
			}),
			transformResponse: (response: any) => {
				const token = response?.token ?? response?.data?.token;
				return token ? decodeToken(token) : (response?.data ?? response?.message ?? []);
			},
			providesTags: ['EmergencyButton'],
		}),
		createEmergencyButton: builder.mutation({
			query: (data) => {
				const token = encodeToken(data);
				return {
					url: `/api/v1/emergency-buttons`,
					method: 'POST',
					body: { token },
				};
			},
			invalidatesTags: ['EmergencyButton'],
		}),
		updateEmergencyButton: builder.mutation({
			query: (data) => {
				const { emergencyButtonId } = data;
				const token = encodeToken(data);
				return {
					url: `/api/v1/emergency-buttons/${emergencyButtonId}`,
					method: 'PATCH',
					body: { token },
				};
			},
			invalidatesTags: (result, error, arg) => [{ type: 'EmergencyButton' as const, id: arg.emergencyButtonId }, 'EmergencyButton'],
		}),
		deleteEmergencyButton: builder.mutation({
			query: (emergencyButtonId) => ({
				url: `/api/v1/emergency-buttons/${emergencyButtonId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['EmergencyButton'],
		}),
		findEmergencyButton: builder.mutation({
			query: (imei) => ({
				url: `/api/v1/emergencybutton/socket/find/${encodeURIComponent(String(imei).replace(/\D/g, ''))}`,
				method: 'POST',
			}),
		}),
		assignEmergencyButton: builder.mutation<any, { emergencyButtonId: string | number; centerId?: any; patientId?: any }>({
			query: ({ emergencyButtonId, centerId, patientId }) => ({
				url: `/api/v1/emergency-buttons/assing/patientCenter/${encodeURIComponent(emergencyButtonId)}`,
				method: 'PATCH',
				body: { centerId, patientId },
			}),
			invalidatesTags: (result, error, { emergencyButtonId }) => [
				{ type: 'EmergencyButton' as const, id: emergencyButtonId },
				'EmergencyButton',
				'Patient',
			],
		}),
		assignEmergencyButtonCorporation: builder.mutation<any, { emergencyButtonId: string | number; corporationId?: string | null }>({
			query: ({ emergencyButtonId, corporationId }) => ({
				url: `/api/v1/emergency-buttons/assign/corporation/${encodeURIComponent(emergencyButtonId)}`,
				method: 'PATCH',
				body: { corporationId },
			}),
			invalidatesTags: (result, error, { emergencyButtonId }) => [
				{ type: 'EmergencyButton' as const, id: emergencyButtonId },
				'EmergencyButton',
			],
		}),
		setSOSNumbersEmergencyButton: builder.mutation<any, { imei: string; phoneNumbers: string[] }>({
			query: ({ imei, phoneNumbers }) => ({
				url: `/api/v1/emergency-buttons/socket/SOSNumberSetting/${encodeURIComponent(imei)}`,
				method: 'POST',
				body: { phoneNumbers },
			}),
			invalidatesTags: (result, error, { imei }) => [{ type: 'EmergencyButton' as const, id: imei }],
		}),
		setCenterSmsNumberEmergencyButton: builder.mutation<any, { imei: string; phoneNumber: string }>({
			query: ({ imei, phoneNumber }) => ({
				url: `/api/v1/emergency-buttons/socket/SMSAlarmNumber/${encodeURIComponent(imei)}`,
				method: 'POST',
				body: { phoneNumber },
			}),
			invalidatesTags: (result, error, { imei }) => [{ type: 'EmergencyButton' as const, id: imei }],
		}),
		requestVideoCallEmergencyButton: builder.mutation<any, string>({
			query: (imei) => ({
				url: `/api/v1/emergency-buttons/socket/videocall/send-request/${encodeURIComponent(imei)}`,
				method: 'POST',
			}),
		}),
		forceEmergencyButtonLocation: builder.mutation<any, string>({
			query: (imei) => ({
				url: `/api/v1/emergency-buttons/socket/location/${encodeURIComponent(String(imei).replace(/\D/g, ''))}`,
				method: 'POST',
			}),
		}),
	}),
});

export const {
	useGetAllEmergencyButtonsQuery,
	useGetEmergencyButtonByIdQuery,
	useGetEmergencyButtonByImeiQuery,
	useGetAllEmergencyButtonsToCenterQuery,
	useCreateEmergencyButtonMutation,
	useUpdateEmergencyButtonMutation,
	useDeleteEmergencyButtonMutation,
	useFindEmergencyButtonMutation,
	useAssignEmergencyButtonMutation,
	useAssignEmergencyButtonCorporationMutation,
	useSetSOSNumbersEmergencyButtonMutation,
	useSetCenterSmsNumberEmergencyButtonMutation,
	useRequestVideoCallEmergencyButtonMutation,
	useForceEmergencyButtonLocationMutation,
} = emergencyButtonApi;

